import { Helmet } from 'react-helmet-async';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircle, Zap, FileText, Users, Shield, Clock,
  ArrowRight, BookOpen, Target, ChevronDown, ChevronUp,
  Star, Download, Brain, Award, TrendingUp, Menu, X
} from 'lucide-react';

const STATS = [
  { value: '10.000+', label: 'Guru Aktif', icon: Users },
  { value: '250.000+', label: 'Dokumen Dibuat', icon: FileText },
  { value: '4.9/5', label: 'Rating Pengguna', icon: Star },
  { value: '80%', label: 'Hemat Waktu', icon: Clock },
];

const PROBLEMS = [
  { icon: Clock, title: 'Memakan Waktu Berjam-jam', desc: 'Menyusun Modul Ajar dan perangkat pembelajaran Kurikulum Merdeka secara manual bisa memakan 10–15 jam per minggu.' },
  { icon: FileText, title: 'Format Modul Ajar Kompleks', desc: 'Permendikbudristek terbaru punya format Modul Ajar dan komponen yang sangat detail dan wajib dipenuhi.' },
  { icon: Target, title: 'Sulit Menyesuaikan CP/ATP', desc: 'Mencocokkan Capaian Pembelajaran dengan Alur Tujuan Pembelajaran untuk Kurikulum Merdeka sangat membingungkan.' },
];

const SOLUTIONS = [
  { icon: Zap, title: 'Generate Modul Ajar dalam 5 Menit', desc: 'AI kami membuat Modul Ajar lengkap berdasarkan jenjang, kelas, dan mata pelajaran Anda sesuai Kurikulum Merdeka.' },
  { icon: CheckCircle, title: 'Sesuai Permendikbudristek', desc: 'Semua Modul Ajar, KKTP, Promes, dan Prota mengikuti format resmi dan komponen yang diwajibkan secara otomatis.' },
  { icon: BookOpen, title: 'Database CP/ATP Lengkap', desc: 'Sistem kami memiliki database Capaian Pembelajaran resmi untuk semua jenjang dan mapel Kurikulum Merdeka.' },
];

const HOW_TO = [
  { step: 1, title: 'Pilih Jenis Dokumen', desc: 'Pilih dokumen yang ingin dibuat: Modul Ajar, KKTP, Promes, Prota, atau Jadwal. Tentukan jenjang dan kelas.' },
  { step: 2, title: 'Input Data Pembelajaran', desc: 'Pilih mata pelajaran, semester, tahun ajaran, dan elemen pembelajaran Kurikulum Merdeka. Tambahkan catatan khusus jika diperlukan.' },
  { step: 3, title: 'Generate & Download', desc: 'AI membuat Modul Ajar lengkap dalam hitungan detik. Review, edit jika perlu, lalu download Word siap cetak.' },
];

const FEATURES = [
  { icon: Brain, title: 'AI Generator Modul Ajar', desc: 'Teknologi AI terbaru menghasilkan Modul Ajar berkualitas tinggi yang sesuai konteks pembelajaran Kurikulum Merdeka.' },
  { icon: Download, title: 'Export ke Word', desc: 'Semua Modul Ajar, KKTP, Promes, dan Prota dapat didownload dalam format Microsoft Word (.docx) siap cetak.' },
  { icon: Users, title: 'Approval Workflow', desc: 'Kepala sekolah dapat mereview, memberi catatan revisi, dan menyetujui Modul Ajar sebelum digunakan.' },
  { icon: Shield, title: 'Data Aman & Terenkripsi', desc: 'Semua data disimpan dengan enkripsi tingkat bank. Akses terbatas dengan JWT dan role-based permission.' },
  { icon: BookOpen, title: 'Database CP/ATP Nasional', desc: 'Akses lengkap ke database Capaian Pembelajaran dan ATP resmi Kurikulum Merdeka untuk semua jenjang dan mata pelajaran.' },
  { icon: TrendingUp, title: 'Update Berkelanjutan', desc: 'Kami selalu update dengan peraturan terbaru Kurikulum Merdeka dari Kemendikbudristek dan tambah fitur berdasarkan feedback.' },
];

const TESTIMONIALS = [
  {
    name: 'Sari Wahyuni, S.Pd',
    school: 'SMPN 3 Surabaya',
    role: 'Guru IPA',
    quote: 'Luar biasa! Modul Ajar yang biasanya saya buat 3 jam, sekarang selesai dalam 5 menit. Hasilnya pun sesuai Kurikulum Merdeka dan siap langsung saya pakai.',
    rating: 5,
    avatar: '👩‍🏫',
  },
  {
    name: 'Budi Santoso, M.Pd',
    school: 'SDN 7 Bandung',
    role: 'Guru Bahasa Indonesia',
    quote: 'Luar biasa! Modul Ajar yang dihasilkan sangat lengkap dan sesuai Kurikulum Merdeka. Hemat waktu berjam-jam setiap minggunya. Harganya juga sangat terjangkau.',
    rating: 5,
    avatar: '👨‍💼',
  },
  {
    name: 'Dewi Rahayu, S.Pd',
    school: 'SMAN 1 Makassar',
    role: 'Guru Matematika',
    quote: 'Awalnya ragu, tapi setelah coba langsung ketagihan. Database CP/ATP-nya lengkap banget, tidak perlu cari-cari sendiri di dokumen pemerintah.',
    rating: 5,
    avatar: '👩‍🏫',
  },
];

const COIN_PACKAGES = [
  {
    id: 'starter',
    label: 'Pemula',
    coins: 15,
    bonus: 0,
    price: 37500,
    highlight: false,
    tag: null,
    perks: ['Cukup untuk ±2 dokumen', 'Modul Ajar, KKTP, Promes', 'Export Word langsung', 'Tidak ada expired'],
  },
  {
    id: 'standard',
    label: 'Standar',
    coins: 35,
    bonus: 5,
    price: 80000,
    highlight: false,
    tag: null,
    perks: ['Cukup untuk ±7–8 dokumen', 'Semua jenis dokumen', '+5 koin bonus gratis', 'Tidak ada expired'],
  },
  {
    id: 'hemat',
    label: 'Hemat',
    coins: 65,
    bonus: 15,
    price: 140000,
    highlight: true,
    tag: '⭐ Terpopuler',
    perks: ['Cukup untuk ±14–16 dokumen', '1 semester penuh cukup!', '+15 koin bonus gratis', 'Paling hemat per koin'],
  },
  {
    id: 'pro',
    label: 'Pro',
    coins: 120,
    bonus: 30,
    price: 240000,
    highlight: false,
    tag: null,
    perks: ['Cukup untuk 1 tahun penuh', '+30 koin bonus gratis', 'Ideal guru produktif', 'Tidak ada expired'],
  },
];


const FAQS = [
  { q: 'Apa itu Coin Guru?', a: 'Coin Guru adalah mata uang digital di Rencana Guru. 1 Coin Guru mulai dari Rp 1.600 (paket Pro) hingga Rp 2.500 (paket Pemula). Anda beli koin sekali, lalu pakai untuk generate dokumen kapan saja. Tidak ada tagihan bulanan, tidak ada langganan — bayar hanya saat butuh.' },
  { q: 'Berapa koin yang saya dapat saat daftar?', a: 'Setiap guru yang baru mendaftar mendapatkan 10 Coin Guru GRATIS — langsung bisa dipakai untuk membuat 2 dokumen pertama tanpa keluar uang sepeser pun. Gunakan kode referral saat daftar untuk dapat +5 koin bonus tambahan!' },
  { q: 'Apakah koin bisa expired?', a: 'Tidak! Saldo Coin Guru Anda tidak pernah expired. Topup sekali, pakai kapan saja — bulan depan, semester depan, atau tahun depan sekalipun.' },
  { q: 'Apakah dokumen yang dihasilkan sesuai Kurikulum Merdeka?', a: 'Tentu! Semua dokumen mengikuti standar Kurikulum Merdeka dan Permendikbudristek terbaru — termasuk Capaian Pembelajaran (CP), ATP, Fase, dan Profil Pelajar Pancasila.' },
  { q: 'Format file apa yang bisa di-download?', a: 'Modul Ajar dan semua dokumen pembelajaran dapat didownload dalam format Microsoft Word (.docx) yang siap cetak dan bisa diedit offline sesuai kebutuhan Anda.' },
  { q: 'Bisakah dokumen diedit setelah di-generate?', a: 'Bisa! Setelah AI men-generate dokumen, Anda bisa download langsung ke Word dan edit sesuka hati. AI memberikan draft terbaik, Anda sempurnakan sesuai konteks kelas.' },
];

const FAQItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-blue-100 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-5 text-left bg-white hover:bg-blue-50 transition-colors"
      >
        <span className="font-semibold text-slate-800 pr-4">{q}</span>
        {open ? <ChevronUp className="flex-shrink-0 text-blue-600" size={20} /> : <ChevronDown className="flex-shrink-0 text-slate-400" size={20} />}
      </button>
      {open && (
        <div className="px-5 pb-5 bg-white">
          <p className="text-slate-600 leading-relaxed">{a}</p>
        </div>
      )}
    </div>
  );
};

export const Landing = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <>
      <Helmet>
        <title>RencanaGuru.com — Buat Modul Ajar AI dalam 5 Menit untuk Guru Indonesia</title>
        <meta name="description" content="Platform AI untuk guru Indonesia membuat Modul Ajar, KKTP, Promes, Prota sesuai Kurikulum Merdeka secara otomatis dalam 5 menit. Daftar gratis sekarang." />
        <link rel="canonical" href="https://rencanaguru.com/" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "RencanaGuru.com",
          "url": "https://rencanaguru.com",
          "description": "Platform AI untuk guru Indonesia membuat Modul Ajar, KKTP, Promes, Prota sesuai Kurikulum Merdeka secara otomatis.",
          "applicationCategory": "EducationalApplication",
          "operatingSystem": "Web",
          "offers": { "@type": "Offer", "price": "0", "priceCurrency": "IDR" },
          "publisher": { "@type": "Organization", "name": "RencanaGuru.com", "url": "https://rencanaguru.com", "logo": "https://rencanaguru.com/logo.png" },
          "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.9", "ratingCount": "10000" }
        })}</script>
      </Helmet>
      <div className="min-h-screen" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>

      {/* ── NAVBAR ── */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b border-blue-100 shadow-sm">
        <div className="container mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.png" alt="RencanaGuru" className="h-10 w-10 object-contain" />
            <span className="font-bold text-xl text-blue-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
              RencanaGuru<span className="text-yellow-500">.com</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-6 text-sm font-medium text-slate-600">
            <a href="#fitur" className="hover:text-blue-700 transition-colors">Fitur</a>
            <a href="#cara-kerja" className="hover:text-blue-700 transition-colors">Cara Kerja</a>
            <a href="#harga" className="hover:text-blue-700 transition-colors">Harga</a>
            <a href="#faq" className="hover:text-blue-700 transition-colors">FAQ</a>
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <Link to="/login" data-testid="login-link" className="px-4 py-2 text-slate-700 hover:text-blue-700 font-medium transition-colors text-sm">
              Masuk
            </Link>
            <Link to="/register" data-testid="register-link"
              className="px-5 py-2 rounded-lg text-white text-sm font-semibold shadow-sm transition-all hover:shadow-md hover:-translate-y-0.5"
              style={{ background: 'linear-gradient(135deg, #1E3A8A, #3B82F6)' }}>
              Daftar Gratis
            </Link>
          </div>

          {/* Mobile menu button */}
          <button className="md:hidden p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X size={24} className="text-slate-700" /> : <Menu size={24} className="text-slate-700" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-blue-100 px-6 py-4 flex flex-col gap-4">
            <a href="#fitur" className="text-slate-700 font-medium py-2" onClick={() => setMobileMenuOpen(false)}>Fitur</a>
            <a href="#cara-kerja" className="text-slate-700 font-medium py-2" onClick={() => setMobileMenuOpen(false)}>Cara Kerja</a>
            <a href="#harga" className="text-slate-700 font-medium py-2" onClick={() => setMobileMenuOpen(false)}>Harga</a>
            <a href="#faq" className="text-slate-700 font-medium py-2" onClick={() => setMobileMenuOpen(false)}>FAQ</a>
            <div className="flex flex-col gap-2 pt-2 border-t border-slate-100">
              <Link to="/login" className="px-4 py-2 text-center border border-slate-200 rounded-lg text-slate-700 font-medium">Masuk</Link>
              <Link to="/register" className="px-4 py-2 text-center rounded-lg text-white font-semibold"
                style={{ background: 'linear-gradient(135deg, #1E3A8A, #3B82F6)' }}>Daftar Gratis</Link>
            </div>
          </div>
        )}
      </header>

      {/* ── HERO ── */}
      <section className="relative overflow-hidden" style={{ background: 'linear-gradient(160deg, #EEF2FF 0%, #DBEAFE 50%, #ECFDF5 100%)' }}>
        <div className="absolute inset-0 opacity-30"
          style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, #BFDBFE 0%, transparent 50%), radial-gradient(circle at 80% 20%, #A5F3FC 0%, transparent 50%)' }} />
        <div className="relative container mx-auto px-6 py-16 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">

            {/* Left: Text */}
            <div className="flex flex-col gap-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 border border-blue-200 rounded-full text-sm font-semibold text-blue-700 w-fit shadow-sm">
                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                ✨ Teknologi AI Terkini untuk Guru Indonesia
              </div>

              <h1 className="text-4xl lg:text-5xl font-bold text-blue-900 leading-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Buat Modul Ajar{' '}
                <span style={{ background: 'linear-gradient(135deg, #1E3A8A, #3B82F6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                  dengan AI
                </span>{' '}
                dalam 5 Menit
              </h1>

              <p className="text-lg text-slate-600 leading-relaxed">
                Platform SaaS terpercaya untuk guru Indonesia. Susun Modul Ajar, KKTP, Promes, dan Prota sesuai{' '}
                <span className="font-semibold text-blue-700">Kurikulum Merdeka</span> — otomatis, cepat, dan siap cetak.
              </p>

              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/register" data-testid="cta-register-button"
                  className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-xl text-white font-semibold text-base shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all"
                  style={{ background: 'linear-gradient(135deg, #1E3A8A, #3B82F6)' }}>
                  Daftar & Klaim 20 Koin Gratis
                  <ArrowRight size={18} />
                </Link>
                <a href="#cara-kerja"
                  className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-xl bg-white border-2 border-blue-200 text-blue-800 font-semibold text-base hover:bg-blue-50 transition-colors">
                  Lihat Cara Kerja
                </a>
              </div>

              <div className="flex flex-wrap gap-3 pt-1">
                <span className="flex items-center gap-1.5 text-sm text-slate-600 bg-white/70 px-3 py-1.5 rounded-full border border-blue-100">
                  🎁 10 Coin Guru gratis saat daftar
                </span>
                <span className="flex items-center gap-1.5 text-sm text-slate-600 bg-white/70 px-3 py-1.5 rounded-full border border-blue-100">
                  <CheckCircle size={14} className="text-green-500" /> Tanpa berlangganan
                </span>
                <span className="flex items-center gap-1.5 text-sm text-slate-600 bg-white/70 px-3 py-1.5 rounded-full border border-blue-100">
                  <CheckCircle size={14} className="text-green-500" /> Sesuai Kurikulum Merdeka
                </span>
              </div>
            </div>

            {/* Right: Product mockup */}
            <div className="relative">
              <div className="bg-white/80 backdrop-blur-sm border border-blue-100 rounded-2xl shadow-xl p-6 flex flex-col gap-4">

                {/* Mockup header */}
                <div className="flex items-center gap-2 pb-3 border-b border-slate-100">
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-red-400" />
                    <div className="w-3 h-3 rounded-full bg-yellow-400" />
                    <div className="w-3 h-3 rounded-full bg-green-400" />
                  </div>
                  <div className="flex-1 bg-slate-100 rounded-full px-3 py-1 text-xs text-slate-500 text-center">
                    rencanaguru.com
                  </div>
                </div>

                {/* Form mock */}
                <div className="flex flex-col gap-3">
                  <div className="text-sm font-semibold text-slate-700">⚡ Generate Dokumen Baru</div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                      <div className="text-xs text-blue-500 mb-0.5">Jenis Dokumen</div>
                      <div className="text-sm font-medium text-blue-900">Modul Ajar</div>
                    </div>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                      <div className="text-xs text-blue-500 mb-0.5">Jenjang</div>
                      <div className="text-sm font-medium text-blue-900">SMP – Kelas 7</div>
                    </div>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                      <div className="text-xs text-blue-500 mb-0.5">Mata Pelajaran</div>
                      <div className="text-sm font-medium text-blue-900">IPA</div>
                    </div>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                      <div className="text-xs text-blue-500 mb-0.5">Semester</div>
                      <div className="text-sm font-medium text-blue-900">Ganjil 2025/2026</div>
                    </div>
                  </div>
                </div>

                {/* Progress/Result mock */}
                <div className="bg-gradient-to-r from-blue-900 to-blue-600 rounded-xl p-4 text-white">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold">AI sedang menyusun...</span>
                    <span className="text-xs text-blue-200">97%</span>
                  </div>
                  <div className="w-full bg-blue-800 rounded-full h-2">
                    <div className="bg-yellow-400 rounded-full h-2" style={{ width: '97%' }} />
                  </div>
                  <div className="mt-2 text-xs text-blue-200">Menyusun langkah-langkah pembelajaran...</div>
                </div>

                {/* Document preview */}
                <div className="border border-slate-200 rounded-xl p-4 flex flex-col gap-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-semibold text-slate-800">📄 ModulAjar_IPA_Kelas7_Ganjil.docx</div>
                      <div className="text-xs text-slate-500">Selesai dibuat • 3 detik yang lalu</div>
                    </div>
                    <span className="bg-green-100 text-green-700 text-xs font-semibold px-2.5 py-1 rounded-full">✓ Siap</span>
                  </div>
                  <div className="flex gap-2 mt-1">
                    <div className="flex-1 bg-blue-600 text-white text-xs font-semibold py-2 rounded-lg text-center cursor-default select-none">
                      ⬇ Download Word
                    </div>
                    <div className="flex-1 bg-slate-100 text-slate-700 text-xs font-semibold py-2 rounded-lg text-center cursor-default select-none">
                      👁 Preview
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating badge */}
              <div className="absolute -top-4 -right-4 bg-yellow-400 text-yellow-900 font-bold text-xs px-3 py-2 rounded-xl shadow-lg rotate-3">
                ⚡ Selesai 5 detik!
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── STATS ── */}
      <section className="bg-white py-12 border-b border-slate-100">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto text-center">
            {STATS.map(({ value, label, icon: Icon }) => (
              <div key={label} className="flex flex-col items-center gap-2">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-1"
                  style={{ background: 'linear-gradient(135deg, #EEF2FF, #DBEAFE)' }}>
                  <Icon size={22} className="text-blue-700" />
                </div>
                <div className="text-3xl font-bold text-blue-900" style={{ fontFamily: "'Outfit', sans-serif" }}>{value}</div>
                <div className="text-sm text-slate-500">{label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PROBLEM & SOLUTION ── */}
      <section className="py-20" style={{ background: 'linear-gradient(160deg, #F8FAFC 0%, #EEF2FF 100%)' }}>
        <div className="container mx-auto px-6">
          <div className="text-center mb-14">
            <div className="inline-block px-4 py-1.5 bg-blue-100 text-blue-700 text-sm font-semibold rounded-full mb-3">Masalah & Solusi</div>
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Kami Paham Tantangan Anda
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-10 items-start max-w-5xl mx-auto">
            {/* Problems */}
            <div className="flex flex-col gap-5">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center text-lg">😩</div>
                <h3 className="text-xl font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>Tantangan Saat Ini</h3>
              </div>
              {PROBLEMS.map(({ icon: Icon, title, desc }) => (
                <div key={title} className="flex gap-4 bg-white border border-red-100 rounded-xl p-5 shadow-sm">
                  <div className="w-11 h-11 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon size={22} className="text-red-500" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-800 mb-1">{title}</h4>
                    <p className="text-sm text-slate-500 leading-relaxed">{desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Solutions */}
            <div className="flex flex-col gap-5">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center text-lg">🚀</div>
                <h3 className="text-xl font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>Solusi Rencana Guru</h3>
              </div>
              {SOLUTIONS.map(({ icon: Icon, title, desc }) => (
                <div key={title} className="flex gap-4 bg-white border border-green-100 rounded-xl p-5 shadow-sm">
                  <div className="w-11 h-11 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon size={22} className="text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-800 mb-1">{title}</h4>
                    <p className="text-sm text-slate-500 leading-relaxed">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section id="cara-kerja" className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-14">
            <div className="inline-block px-4 py-1.5 bg-blue-100 text-blue-700 text-sm font-semibold rounded-full mb-3">Cara Kerja</div>
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
              3 Langkah Mudah
            </h2>
            <p className="text-slate-500 mt-3 max-w-xl mx-auto">Tidak perlu training khusus. Siapapun bisa langsung pakai.</p>
          </div>
          <div className="max-w-5xl mx-auto grid md:grid-cols-3 gap-8 relative">
            {/* Connector line */}
            <div className="hidden md:block absolute top-12 left-1/3 right-1/3 h-0.5" style={{ background: 'linear-gradient(90deg, #3B82F6, #3B82F6)', opacity: 0.2 }} />
            {HOW_TO.map(({ step, title, desc }) => (
              <div key={step} className="relative flex flex-col items-center text-center gap-4 p-8 rounded-2xl border border-blue-100 bg-gradient-to-b from-white to-blue-50 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-2xl font-bold text-white shadow-md"
                  style={{ background: 'linear-gradient(135deg, #1E3A8A, #3B82F6)' }}>
                  {step}
                </div>
                <h3 className="text-lg font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>{title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/register"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl text-white font-semibold shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all"
              style={{ background: 'linear-gradient(135deg, #1E3A8A, #3B82F6)' }}>
              Mulai Sekarang Gratis
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* ── FEATURES ── */}
      <section id="fitur" className="py-20" style={{ background: 'linear-gradient(160deg, #EEF2FF 0%, #ECFDF5 100%)' }}>
        <div className="container mx-auto px-6">
          <div className="text-center mb-14">
            <div className="inline-block px-4 py-1.5 bg-blue-100 text-blue-700 text-sm font-semibold rounded-full mb-3">Fitur Unggulan</div>
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Semua yang Anda Butuhkan
            </h2>
            <p className="text-slate-500 mt-3 max-w-xl mx-auto">Dirancang khusus untuk guru Indonesia yang ingin bekerja lebih efisien.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {FEATURES.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="bg-white/80 backdrop-blur-sm border border-blue-100 rounded-2xl p-6 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                  style={{ background: 'linear-gradient(135deg, #EEF2FF, #DBEAFE)' }}>
                  <Icon size={24} className="text-blue-700" />
                </div>
                <h3 className="text-lg font-bold text-slate-800 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>{title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-14">
            <div className="inline-block px-4 py-1.5 bg-yellow-100 text-yellow-700 text-sm font-semibold rounded-full mb-3">Testimoni</div>
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Dipercaya Guru di Seluruh Indonesia
            </h2>
            <p className="text-slate-500 mt-3">Bergabung dengan 10.000+ guru yang sudah merasakan manfaatnya.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {TESTIMONIALS.map(({ name, school, role, quote, rating, avatar }) => (
              <div key={name} className="flex flex-col gap-4 p-7 rounded-2xl border border-blue-100 bg-gradient-to-b from-white to-blue-50 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex gap-1">
                  {Array.from({ length: rating }).map((_, i) => (
                    <Star key={i} size={16} className="text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <p className="text-slate-600 leading-relaxed text-sm italic flex-1">"{quote}"</p>
                <div className="flex items-center gap-3 pt-3 border-t border-blue-100">
                  <div className="w-11 h-11 rounded-full flex items-center justify-center text-2xl"
                    style={{ background: 'linear-gradient(135deg, #EEF2FF, #DBEAFE)' }}>
                    {avatar}
                  </div>
                  <div>
                    <div className="font-semibold text-slate-800 text-sm">{name}</div>
                    <div className="text-xs text-slate-500">{role} · {school}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PRICING (COIN GURU) ── */}
      <section id="harga" className="py-20" style={{ background: 'linear-gradient(160deg, #F8FAFC 0%, #EEF2FF 100%)' }}>
        <div className="container mx-auto px-6">

          {/* Header */}
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-yellow-100 text-yellow-700 text-sm font-semibold rounded-full mb-3">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><defs><radialGradient id="cg1" cx="40%" cy="35%" r="60%"><stop offset="0%" stopColor="#FDE68A"/><stop offset="40%" stopColor="#F59E0B"/><stop offset="100%" stopColor="#B45309"/></radialGradient></defs><circle cx="12" cy="12" r="9" fill="url(#cg1)"/><circle cx="12" cy="12" r="9" fill="none" stroke="#B45309" strokeWidth="0.8" opacity="0.6"/><circle cx="12" cy="12" r="7" fill="none" stroke="#FCD34D" strokeWidth="0.6" opacity="0.7"/><text x="12" y="16.5" textAnchor="middle" fontSize="9" fontWeight="bold" fontFamily="serif" fill="#92400E" opacity="0.85">₵</text></svg>
              Coin Guru
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Bayar Sesuai Kebutuhan,<br />Tanpa Berlangganan
            </h2>
            <p className="text-slate-500 mt-3 max-w-xl mx-auto">
              Beli Coin Guru sekali, pakai kapan saja. Tidak ada tagihan bulanan — koin tidak pernah expired.
            </p>
            {/* Free coins badge */}
            <div className="inline-flex items-center gap-2 mt-5 px-5 py-2.5 rounded-full text-sm font-bold text-white shadow-lg"
              style={{ background: 'linear-gradient(135deg, #F59E0B, #EF4444)' }}>
              🎁 Daftar Sekarang — Dapat 10 Coin Guru GRATIS!
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto mb-14">
            {COIN_PACKAGES.map(({ id, label, coins, bonus, price, highlight, tag, perks }) => {
              const total = coins + bonus;
              const perCoin = Math.round(price / total);
              return (
                <div key={id}
                  className={`relative flex flex-col rounded-2xl p-6 transition-all hover:shadow-lg ${
                    highlight
                      ? 'border-2 border-blue-500 bg-white shadow-md'
                      : 'border border-slate-200 bg-white hover:border-blue-200'
                  }`}>
                  {tag && (
                    <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-xs font-bold text-white whitespace-nowrap"
                      style={{ background: 'linear-gradient(135deg, #1E3A8A, #3B82F6)' }}>
                      {tag}
                    </div>
                  )}
                  <div className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-3">{label}</div>

                  {/* Coin amount */}
                  <div className="mb-1">
                    <span className="text-5xl font-bold text-blue-900" style={{ fontFamily: "'Outfit', sans-serif" }}>{total}</span>
                    <span className="text-slate-500 text-base ml-1">koin</span>
                  </div>
                  {bonus > 0 && (
                    <div className="text-xs text-green-600 font-semibold mb-1">{coins} koin + {bonus} bonus gratis</div>
                  )}
                  <div className="text-slate-400 text-xs mb-4">Rp {perCoin.toLocaleString('id-ID')}/koin</div>

                  {/* Price */}
                  <div className="text-2xl font-bold text-slate-800 mb-5" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    Rp {price.toLocaleString('id-ID')}
                  </div>

                  <ul className="flex flex-col gap-2 mb-6 flex-1">
                    {perks.map(p => (
                      <li key={p} className="flex items-start gap-2 text-xs text-slate-600">
                        <CheckCircle size={13} className="text-green-500 flex-shrink-0 mt-0.5" />
                        {p}
                      </li>
                    ))}
                  </ul>

                  <Link to="/register"
                    className="block w-full py-3 rounded-xl text-center font-bold text-sm text-white transition-all hover:shadow-md hover:-translate-y-0.5"
                    style={{ background: highlight ? 'linear-gradient(135deg, #1E3A8A, #3B82F6)' : 'linear-gradient(135deg, #374151, #1F2937)' }}>
                    Mulai Pakai
                  </Link>
                </div>
              );
            })}
          </div>


        </div>
      </section>

      {/* ── FAQ ── */}
      <section id="faq" className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-14">
            <div className="inline-block px-4 py-1.5 bg-blue-100 text-blue-700 text-sm font-semibold rounded-full mb-3">FAQ</div>
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Pertanyaan yang Sering Ditanya
            </h2>
          </div>
          <div className="max-w-3xl mx-auto flex flex-col gap-3">
            {FAQS.map(({ q, a }) => (
              <FAQItem key={q} q={q} a={a} />
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA PENUTUP ── */}
      <section className="py-24 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #1E3A8A 0%, #1D4ED8 50%, #3B82F6 100%)' }}>
        <div className="absolute inset-0 opacity-10"
          style={{ backgroundImage: 'radial-gradient(circle at 30% 50%, #FFFFFF 0%, transparent 60%), radial-gradient(circle at 70% 50%, #FCD34D 0%, transparent 60%)' }} />
        <div className="relative container mx-auto px-6 text-center">
          <div className="inline-block px-4 py-1.5 bg-white/20 border border-white/30 text-white text-sm font-semibold rounded-full mb-6">
            🎁 Daftar Gratis — Langsung Dapat 10 Coin Guru!
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 leading-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Siap Menghemat Waktu<br />Persiapan Mengajar?
          </h2>
          <p className="text-blue-100 text-lg mb-10 max-w-2xl mx-auto leading-relaxed">
            Daftar sekarang dan langsung dapat <strong>10 Coin Guru gratis</strong> — cukup untuk 2 dokumen pertama Anda. Tidak perlu kartu kredit.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-yellow-400 hover:bg-yellow-300 text-yellow-900 rounded-xl font-bold text-lg shadow-xl hover:shadow-2xl hover:-translate-y-1 transition-all">
              Daftar & Klaim 20 Koin Gratis
              <ArrowRight size={20} />
            </Link>
            <Link to="/login"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 hover:bg-white/20 border border-white/30 text-white rounded-xl font-semibold text-lg transition-all">
              Sudah punya akun? Masuk
            </Link>
          </div>

          {/* Social proof */}
          <div className="mt-12 flex flex-wrap justify-center items-center gap-6 text-blue-200 text-sm">
            <span className="flex items-center gap-1.5"><CheckCircle size={16} className="text-green-400" /> 20 koin gratis saat daftar</span>
            <span className="flex items-center gap-1.5"><CheckCircle size={16} className="text-green-400" /> Koin tidak pernah expired</span>
            <span className="flex items-center gap-1.5"><CheckCircle size={16} className="text-green-400" /> Tanpa berlangganan bulanan</span>
            <span className="flex items-center gap-1.5"><CheckCircle size={16} className="text-green-400" /> Data aman & terenkripsi</span>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="bg-blue-950 text-white py-14">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-10 mb-10">
            <div className="md:col-span-1">
              <div className="flex items-center gap-2 mb-4">
                <img src="/logo-white.png" alt="RencanaGuru" className="h-10 w-10 object-contain" />
                <span className="font-bold text-lg" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  RencanaGuru<span className="text-yellow-400">.com</span>
                </span>
              </div>
              <p className="text-blue-300 text-sm leading-relaxed">
                Platform AI terpercaya untuk guru Indonesia menyusun perangkat pembelajaran sesuai Kurikulum Merdeka.
              </p>
            </div>
            <div>
              <h4 className="font-bold mb-4 text-sm uppercase tracking-wider text-blue-400">Produk</h4>
              <ul className="space-y-2.5 text-sm text-blue-300">
                <li><a href="#fitur" className="hover:text-white transition-colors">Fitur</a></li>
                <li><a href="#harga" className="hover:text-white transition-colors">Harga</a></li>
                <li><a href="#cara-kerja" className="hover:text-white transition-colors">Cara Kerja</a></li>
                <li><a href="#faq" className="hover:text-white transition-colors">FAQ</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4 text-sm uppercase tracking-wider text-blue-400">Perusahaan</h4>
              <ul className="space-y-2.5 text-sm text-blue-300">
                <li><a href="#" className="hover:text-white transition-colors">Tentang Kami</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Blog</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Karier</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Kontak</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4 text-sm uppercase tracking-wider text-blue-400">Legal & Dukungan</h4>
              <ul className="space-y-2.5 text-sm text-blue-300">
                <li><a href="#" className="hover:text-white transition-colors">Privacy Policy</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Terms of Service</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Pusat Bantuan</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-blue-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-blue-400">
            <p>© 2026 RencanaGuru.com · Platform AI untuk Guru Indonesia</p>
            <div className="flex items-center gap-2">
              <Award size={14} className="text-yellow-400" />
              <span>Sesuai Kurikulum Merdeka & Permendikbudristek</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
    </>
  );
};